// client/src/components/reports/ReportBuilder.jsx
//
// /:tenant/reports/new                 build a report
// /:tenant/reports/:reportId           open a saved report (edit if allowed)
// /:tenant/reports/template/:templateKey  open a locked system template
//
// Steps: dataset → columns (detail rows, or group-by + measures) → filters →
// sort & limit → chart, with a live preview. Collapsible steps stack on
// phones; on desktop the steps sit beside the preview.
//
// The server validates everything (server/reports/definition.js) and decides
// what the user may see: masked PII can't be filtered, grouped or sorted on,
// so the UI doesn't offer it. Heavy datasets preview in the background
// (202 + a run to poll).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome, FaLock, FaRegClock, FaCopy, FaTrash, FaSave, FaShareAlt } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import ReportResult from './ReportResult';
import ExportBar from './ExportBar';
import ScheduleModal from './ScheduleModal';
import ShareModal from './ShareModal';
import useReportRun from './useReportRun';
import {
  AGG_FNS, BUCKETS, NUMERIC, OPS, VIZ, defaultValue, fromStored, isSummary, newDefinition,
  opFamily, previewable, toRequest, valueKind,
} from './reportModel';
import {
  getReportPermissions, getDatasets, getReport, previewReport, createReport, updateReport,
  deleteReport, duplicateReport, materializeTemplate, exportReport, exportDefinition, getRunResult,
  errorText,
} from '../../services/reportsApi';
import '../analytics/PerformancePanel.css';
import '../forms/Forms.css';
import './Reports.css';

const PAGE_SIZE = 50;

function useLabels(t) {
  return useMemo(() => ({
    ops: {
      eq: t('reports.op.eq', 'is'), neq: t('reports.op.neq', 'is not'), in: t('reports.op.in', 'is any of'),
      not_in: t('reports.op.not_in', 'is none of'), contains: t('reports.op.contains', 'contains'),
      not_contains: t('reports.op.not_contains', 'does not contain'), starts_with: t('reports.op.starts_with', 'starts with'),
      gt: '>', gte: '≥', lt: '<', lte: '≤', between: t('reports.op.between', 'between'),
      on: t('reports.op.on', 'on'), before: t('reports.op.before', 'before'), after: t('reports.op.after', 'after'),
      is_empty: t('reports.op.is_empty', 'is empty'), is_not_empty: t('reports.op.is_not_empty', 'is not empty'),
      is_true: t('reports.op.is_true', 'is yes'), is_false: t('reports.op.is_false', 'is no'),
      today: t('reports.op.today', 'today'), yesterday: t('reports.op.yesterday', 'yesterday'),
      last_n_days: t('reports.op.last_n_days', 'in the last N days'), next_n_days: t('reports.op.next_n_days', 'in the next N days'),
      this_week: t('reports.op.this_week', 'this week'), last_week: t('reports.op.last_week', 'last week'),
      this_month: t('reports.op.this_month', 'this month'), last_month: t('reports.op.last_month', 'last month'),
      this_quarter: t('reports.op.this_quarter', 'this quarter'), last_quarter: t('reports.op.last_quarter', 'last quarter'),
      this_year: t('reports.op.this_year', 'this year'), year_to_date: t('reports.op.year_to_date', 'year to date'),
      last_year: t('reports.op.last_year', 'last year'),
    },
    fns: {
      count: t('reports.fn.count', 'Count'), count_distinct: t('reports.fn.count_distinct', 'Count distinct'),
      sum: t('reports.fn.sum', 'Total'), avg: t('reports.fn.avg', 'Average'), min: t('reports.fn.min', 'Minimum'), max: t('reports.fn.max', 'Maximum'),
    },
    buckets: {
      day: t('reports.bucket.day', 'by day'), week: t('reports.bucket.week', 'by week'), month: t('reports.bucket.month', 'by month'),
      quarter: t('reports.bucket.quarter', 'by quarter'), year: t('reports.bucket.year', 'by year'),
    },
    viz: {
      table: t('reports.viz.table', 'Table'), bar: t('reports.viz.bar', 'Bar chart'), line: t('reports.viz.line', 'Line chart'), kpi: t('reports.viz.kpi', 'KPI tiles'),
    },
  }), [t]);
}

/** Server-default alias for a measure, made unique. */
function aliasFor(a, taken) {
  const base = `${a.fn}_${a.field || 'rows'}`;
  let as = base;
  let i = 2;
  while (taken.has(as)) as = `${base}_${i++}`;
  return as;
}

function withAliases(aggregates) {
  const taken = new Set();
  return aggregates.map((a) => {
    const as = aliasFor(a, taken);
    taken.add(as);
    return { ...a, as };
  });
}

/** Output columns as the server will name them (for sort / chart pickers). */
function outputColumns(def, ds, fns) {
  if (!ds) return [];
  const f = (k) => ds.fields.find((x) => x.key === k);
  if (!isSummary(def)) return def.fields.map((k) => f(k)).filter(Boolean).map((x) => ({ key: x.key, label: x.label, type: x.type, masked: x.masked }));
  return [
    ...def.groupBy.filter((g) => f(g.field)).map((g) => ({ key: g.field, label: f(g.field).label, type: g.bucket && g.bucket !== 'year' ? 'string' : f(g.field).type, masked: false })),
    ...def.aggregates.map((a) => ({
      key: a.as,
      label: a.label || (a.field && f(a.field) ? `${fns[a.fn]} · ${f(a.field).label}` : fns.count),
      type: a.fn === 'count' || a.fn === 'count_distinct' ? 'integer' : (f(a.field) || {}).type === 'boolean' ? 'number' : (f(a.field) || {}).type,
      masked: false,
    })),
  ];
}

/**
 * Starting axes for a chart type: y = the measures (summary) or numeric
 * columns (detail); x = the first group-by column, else the first non-numeric.
 */
function defaultViz(type, def, cols, numericCols) {
  if (type === 'table') return { type: 'table' };
  const groupKeys = def.groupBy.map((g) => g.field);
  const x = groupKeys[0] || (cols.find((c) => !NUMERIC.includes(c.type)) || {}).key;
  const y = (isSummary(def) ? def.aggregates.map((a) => a.as) : numericCols.map((c) => c.key))
    .filter((k) => k !== x && numericCols.some((c) => c.key === k))
    .slice(0, type === 'kpi' ? 4 : 2);
  return type === 'kpi' ? { type, y } : { type, x, y };
}

function FilterValue({ field, filter, onChange, t }) {
  const kind = valueKind(filter.op);
  const fam = opFamily(field.type);
  const inputType = fam === 'numeric' ? 'number' : fam === 'date' ? 'date' : 'text';
  const label = t('reports.filters.value', 'Value');
  if (kind === 'none') return null;
  if (kind === 'days') {
    return <input type="number" min="1" max="3650" aria-label={t('reports.filters.days', 'Days')} value={filter.value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  }
  if (kind === 'pair') {
    const [a, b] = Array.isArray(filter.value) ? filter.value : ['', ''];
    return (
      <>
        <input type={inputType} aria-label={t('reports.filters.from', 'From')} value={a} onChange={(e) => onChange([e.target.value, b])} />
        <input type={inputType} aria-label={t('reports.filters.to', 'To')} value={b} onChange={(e) => onChange([a, e.target.value])} />
      </>
    );
  }
  if (kind === 'list') {
    if (field.type === 'enum') {
      const list = Array.isArray(filter.value) ? filter.value : [];
      return (
        <div className="rp-chips" role="group" aria-label={label}>
          {field.options.map((o) => (
            <label key={o} className="rp-chip">
              <input type="checkbox" checked={list.includes(o)} onChange={() => onChange(list.includes(o) ? list.filter((x) => x !== o) : [...list, o])} />
              {o}
            </label>
          ))}
        </div>
      );
    }
    const text = Array.isArray(filter.value) ? filter.value.join(', ') : '';
    return (
      <input
        type="text"
        aria-label={t('reports.filters.list', 'Values, separated by commas')}
        placeholder={t('reports.filters.list', 'Values, separated by commas')}
        defaultValue={text}
        onBlur={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean).map((s) => (fam === 'numeric' ? Number(s) : s)))}
      />
    );
  }
  if (field.type === 'enum') {
    return (
      <select aria-label={label} value={filter.value ?? ''} onChange={(e) => onChange(e.target.value)}>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return <input type={inputType} aria-label={label} value={filter.value ?? ''} onChange={(e) => onChange(fam === 'numeric' && e.target.value !== '' ? Number(e.target.value) : e.target.value)} />;
}

export default function ReportBuilder({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const L = useLabels(t);
  const params = useParams();
  const navigate = useNavigate();
  const tenant = Number(tenantProp || params.tenant);
  const reportId = params.reportId ? Number(params.reportId) : null;
  const templateKey = params.templateKey || null;
  const mode = templateKey ? 'template' : reportId ? 'saved' : 'new';

  const [perms, setPerms] = useState(null);
  const [datasets, setDatasets] = useState(null);
  const [timezone, setTimezone] = useState(null);
  const [report, setReport] = useState(null);
  const [def, setDef] = useState(null);
  const [meta, setMeta] = useState({ name: '', description: '', visibility: 'private' });
  const [loadError, setLoadError] = useState('');
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);
  const [previewError, setPreviewError] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [heavyRunId, setHeavyRunId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scheduleFor, setScheduleFor] = useState(null);
  const [shareFor, setShareFor] = useState(null);
  const previewSeq = useRef(0);

  const editable = mode === 'new' ? !!perms?.create : mode === 'saved' ? !!report?.access?.edit : false;
  const ds = useMemo(() => (datasets && def ? datasets.find((d) => d.key === def.dataset) : null), [datasets, def]);

  // ---- initial load
  const load = useCallback(async () => {
    setLoadError('');
    try {
      const p = await getReportPermissions();
      setPerms(p.permissions);
      if (p.permissions.create || p.permissions.view_shared) {
        const d = await getDatasets();
        setDatasets(d.datasets);
        setTimezone(d.timezone);
        if (mode === 'new') {
          const first = d.datasets[0];
          setDef(first ? newDefinition(first) : null);
        }
      } else {
        setDatasets([]);
      }
      if (mode === 'saved') {
        const r = await getReport(reportId);
        setReport(r.report);
        setDef(fromStored(r.report.definition));
        setMeta({ name: r.report.name, description: r.report.description || '', visibility: r.report.visibility });
      }
      if (mode === 'template') {
        setMeta({ name: '', description: '', visibility: 'shared' });
      }
    } catch (err) {
      setLoadError(errorText(err, t('reports.builder.loadError', 'Could not load this report.')));
    }
  }, [mode, reportId, t]);

  useEffect(() => { load(); }, [load]);

  // ---- preview
  const applyResult = useCallback((res) => {
    // A heavy run's JSON result has no meta; the 202 that queued it does.
    if (mode === 'template' && res.meta) setMeta((m) => (m.name ? m : { name: res.meta.name, description: res.meta.description || '', visibility: 'shared' }));
    if (res.queued) {
      setHeavyRunId(res.runId);
      return;
    }
    setResult(res);
    if (mode === 'template') setDef((cur) => cur || fromStored(res.definition));
  }, [mode]);

  const runPreview = useCallback(async (pg) => {
    const seq = ++previewSeq.current;
    setPreviewing(true);
    setPreviewError('');
    setHeavyRunId(null);
    try {
      let body;
      if (mode === 'template') body = { templateKey, page: pg, pageSize: PAGE_SIZE };
      else if (!editable && mode === 'saved') body = { reportId, page: pg, pageSize: PAGE_SIZE };
      else body = { definition: toRequest(def), page: pg, pageSize: PAGE_SIZE };
      const res = await previewReport(body);
      if (seq !== previewSeq.current) return;
      applyResult(res);
    } catch (err) {
      if (seq !== previewSeq.current) return;
      setResult(null);
      setPreviewError(errorText(err, t('reports.builder.previewError', 'Could not run the preview.')));
    } finally {
      if (seq === previewSeq.current) setPreviewing(false);
    }
  }, [applyResult, def, editable, mode, reportId, t, templateKey]);

  // Live preview: debounced while editing; immediate for read-only views.
  const defKey = def ? JSON.stringify(toRequest(def)) : '';
  useEffect(() => {
    if (!perms) return undefined;
    if (mode === 'template' || (mode === 'saved' && report && !editable)) {
      runPreview(page);
      return undefined;
    }
    if (!def || !previewable(def)) return undefined;
    const h = setTimeout(() => runPreview(page), 600);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defKey, page, perms, report, mode, editable]);

  // Heavy preview: poll the run, then load its JSON result.
  const { run: heavyRun } = useReportRun(heavyRunId);
  useEffect(() => {
    if (!heavyRun) return;
    if (heavyRun.status === 'done') {
      getRunResult(heavyRun.id).then(applyResult).catch(() => setPreviewError(t('reports.builder.previewError', 'Could not run the preview.')));
      setHeavyRunId(null);
    } else if (heavyRun.status === 'failed') {
      setPreviewError(heavyRun.error || t('reports.builder.previewError', 'Could not run the preview.'));
      setHeavyRunId(null);
    }
  }, [heavyRun, applyResult, t]);

  // ---- editing helpers
  const update = (patch) => { setPage(1); setDef((d) => ({ ...d, ...patch })); };
  const field = (k) => (ds ? ds.fields.find((f) => f.key === k) : null);
  const cols = outputColumns(def || { fields: [], groupBy: [], aggregates: [] }, ds, L.fns);
  const numericCols = cols.filter((c) => NUMERIC.includes(c.type) && !c.masked);

  const setSummary = (on) => {
    if (on === isSummary(def)) return;
    if (on) {
      const groupable = ds.fields.find((f) => f.groupable && f.type !== 'datetime') || ds.fields.find((f) => f.groupable);
      update({ groupBy: groupable ? [{ field: groupable.key }] : [], aggregates: withAliases([{ fn: 'count' }]), sort: [], visualization: { type: 'table' } });
    } else {
      update({ groupBy: [], aggregates: [], sort: [], visualization: { type: 'table' }, fields: def.fields.length ? def.fields : newDefinition(ds).fields });
    }
  };

  const changeDataset = (key) => {
    const next = datasets.find((d) => d.key === key);
    if (next) { setPage(1); setDef(newDefinition(next)); setResult(null); }
  };

  const setAgg = (i, patch) => {
    const aggs = def.aggregates.map((a, j) => (j === i ? { ...a, ...patch } : a));
    update({ aggregates: withAliases(aggs), sort: [], visualization: def.visualization.type === 'table' ? def.visualization : { type: def.visualization.type } });
  };

  const fieldsForFn = (fn) => ds.fields.filter((f) => {
    if (fn === 'count' || fn === 'count_distinct') return true;
    if (fn === 'sum' || fn === 'avg') return f.aggregatable;
    return !f.masked && (NUMERIC.includes(f.type) || f.type === 'date' || f.type === 'datetime');
  });

  // ---- actions
  const ensureId = async () => {
    if (mode === 'saved') return reportId;
    if (mode === 'template') {
      const r = await materializeTemplate(templateKey);
      return r.report.id;
    }
    return null;
  };

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const body = { name: meta.name, description: meta.description, visibility: meta.visibility, definition: toRequest(def) };
      if (mode === 'new') {
        const r = await createReport(body);
        navigate(`/${tenant}/reports/${r.report.id}`, { replace: true });
        return;
      }
      const r = await updateReport(reportId, body);
      setReport(r.report);
      setNotice({ kind: 'ok', text: t('reports.builder.saved', 'Saved.') });
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('reports.builder.saveError', 'Could not save the report.')) });
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    try {
      const id = await ensureId();
      const r = await duplicateReport(id);
      navigate(`/${tenant}/reports/${r.report.id}`);
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('reports.builder.duplicateError', 'Could not duplicate the report.')) });
    }
  };

  const remove = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('reports.builder.confirmDelete', 'Delete this report and its schedules?'))) return;
    try {
      await deleteReport(reportId);
      navigate(`/${tenant}/reports`);
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('reports.builder.deleteError', 'Could not delete the report.')) });
    }
  };

  const doExport = async (format) => {
    if (mode === 'new') return exportDefinition(toRequest(def), format, meta.name || (ds && ds.label));
    const id = await ensureId();
    return exportReport(id, format);
  };

  const openSchedule = async () => {
    try {
      setScheduleFor(await ensureId());
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('reports.schedule.loadError', 'Could not load schedules.')) });
    }
  };

  const openShare = async () => {
    try {
      setShareFor(await ensureId());
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('reports.share.loadError', 'Could not load share links.')) });
    }
  };

  // ---- render
  const title = mode === 'new' ? t('reports.builder.newTitle', 'New report')
    : meta.name || (mode === 'template' ? t('reports.builder.templateTitle', 'System template') : t('reports.builder.untitled', 'Report'));

  const footer = (
    <AppFooter
      buttons={[
        { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(`/${tenant}/reports`) },
        { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
      ]}
    />
  );

  if (loadError) return <div className="rp-page"><ErrorState message={loadError} onRetry={load} />{footer}</div>;
  if (perms && mode === 'new' && !perms.create) {
    return <div className="rp-page"><div className="note warn">{t('reports.builder.noCreate', 'You do not have permission to build reports.')}</div>{footer}</div>;
  }
  if (!perms || !datasets || (mode !== 'template' && !def)) {
    if (perms && mode === 'new' && datasets && datasets.length === 0) {
      return <div className="rp-page"><div className="note warn">{t('reports.builder.noDatasets', 'No report data is available yet.')}</div>{footer}</div>;
    }
    return <div className="rp-page"><LoadingState />{footer}</div>;
  }

  const summary = def ? isSummary(def) : false;

  return (
    <div className="rp-page">
      <section className="surface">
        <div className="rp-head">
          <h2>
            {(mode === 'template' || report?.locked) && <FaLock aria-hidden="true" style={{ marginRight: 6, fontSize: '0.8em' }} />}
            {title}
          </h2>
          <div className="rp-actions">
            {editable && (
              <button type="button" className="lm-btn" onClick={save} disabled={saving || !meta.name.trim() || !previewable(def)}>
                <FaSave aria-hidden="true" /> {mode === 'new' ? t('reports.builder.save', 'Save report') : t('reports.builder.saveChanges', 'Save changes')}
              </button>
            )}
            {perms.create && mode !== 'new' && (
              <button type="button" className="lm-btn secondary" onClick={duplicate}>
                <FaCopy aria-hidden="true" /> {t('reports.builder.duplicate', 'Duplicate')}
              </button>
            )}
            {perms.schedule && mode !== 'new' && (
              <button type="button" className="lm-btn secondary" onClick={openSchedule}>
                <FaRegClock aria-hidden="true" /> {t('reports.builder.schedule', 'Schedule')}
              </button>
            )}
            {perms.share && mode !== 'new' && (
              <button type="button" className="lm-btn secondary" onClick={openShare}>
                <FaShareAlt aria-hidden="true" /> {t('reports.builder.share', 'Share')}
              </button>
            )}
            {editable && mode === 'saved' && (
              <button type="button" className="rp-icon-btn" onClick={remove} aria-label={t('reports.builder.delete', 'Delete report')}>
                <FaTrash aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
        {mode === 'template' && (
          <p className="surface-muted perf-small">{t('reports.builder.templateNote', 'System templates are locked so everyone gets the same numbers. Duplicate one to change it.')}</p>
        )}
        {editable && (
          <div className="fo-form-grid" style={{ marginTop: 8 }}>
            <label className="fo-field">
              {t('reports.builder.name', 'Report name')}
              <input type="text" maxLength={150} value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} />
            </label>
            <label className="fo-field">
              {t('reports.builder.visibility', 'Who can see it')}
              <select value={meta.visibility} onChange={(e) => setMeta({ ...meta, visibility: e.target.value })}>
                <option value="private">{t('reports.builder.private', 'Only me')}</option>
                <option value="shared">{t('reports.builder.shared', 'Shared with my team')}</option>
              </select>
            </label>
            <label className="fo-field" style={{ gridColumn: '1 / -1' }}>
              {t('reports.builder.description', 'Description (optional)')}
              <input type="text" maxLength={500} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} />
            </label>
          </div>
        )}
        {!editable && meta.description && <p className="surface-muted perf-small">{meta.description}</p>}
        {notice && <div className={`note ${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.text}</div>}
      </section>

      <div className={editable ? 'rp-builder' : undefined}>
        {editable && ds && (
          <div className="rp-config">
            <details className="rp-step surface" open>
              <summary>{t('reports.builder.stepData', '1. Data')}<span className="rp-step-hint">{ds.label}</span></summary>
              <div className="rp-step-body">
                <label className="fo-field">
                  {t('reports.builder.dataset', 'What do you want to report on?')}
                  <select value={def.dataset} onChange={(e) => changeDataset(e.target.value)}>
                    {datasets.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                  </select>
                </label>
                <p className="surface-muted perf-small" style={{ margin: 0 }}>{ds.description}</p>
                {ds.params.map((p) => (
                  <label key={p.key} className="fo-field">
                    {p.label}
                    <input
                      type="number"
                      min={p.min}
                      max={p.max}
                      value={def.params[p.key] ?? p.default}
                      onChange={(e) => update({ params: { ...def.params, [p.key]: Number(e.target.value) } })}
                    />
                  </label>
                ))}
              </div>
            </details>

            <details className="rp-step surface" open>
              <summary>
                {t('reports.builder.stepColumns', '2. Columns')}
                <span className="rp-step-hint">{summary ? t('reports.builder.summary', 'Summary') : t('reports.builder.detail', 'Detail rows')}</span>
              </summary>
              <div className="rp-step-body">
                <div className="rp-mode" role="group" aria-label={t('reports.builder.modeLabel', 'Report type')}>
                  <button type="button" aria-pressed={!summary} onClick={() => setSummary(false)}>{t('reports.builder.detail', 'Detail rows')}</button>
                  <button type="button" aria-pressed={summary} onClick={() => setSummary(true)}>{t('reports.builder.summary', 'Summary')}</button>
                </div>

                {!summary && (
                  <ul className="rp-fieldlist">
                    {ds.fields.map((f) => (
                      <li key={f.key}>
                        <label>
                          <input
                            type="checkbox"
                            checked={def.fields.includes(f.key)}
                            onChange={() => update({
                              fields: def.fields.includes(f.key) ? def.fields.filter((k) => k !== f.key) : [...def.fields, f.key],
                              sort: def.sort.filter((s) => s.field !== f.key || def.fields.includes(f.key)),
                            })}
                          />
                          {f.label}
                          {f.masked && <span className="rp-masked">{t('reports.builder.masked', 'masked')}</span>}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}

                {summary && (
                  <>
                    <h5 style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#23324d' }}>{t('reports.builder.groupBy', 'Group by')}</h5>
                    {def.groupBy.map((g, i) => {
                      const gf = field(g.field);
                      const isDate = gf && (gf.type === 'date' || gf.type === 'datetime');
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={i} className="rp-row">
                          <select
                            aria-label={t('reports.builder.groupField', 'Group field')}
                            value={g.field}
                            onChange={(e) => {
                              const nf = field(e.target.value);
                              const bucket = nf && (nf.type === 'date' || nf.type === 'datetime') ? 'month' : undefined;
                              update({ groupBy: def.groupBy.map((x, j) => (j === i ? { field: e.target.value, ...(bucket ? { bucket } : {}) } : x)), sort: [] });
                            }}
                          >
                            {ds.fields.filter((f) => f.groupable && (f.key === g.field || !def.groupBy.some((x) => x.field === f.key))).map((f) => (
                              <option key={f.key} value={f.key}>{f.label}</option>
                            ))}
                          </select>
                          {isDate ? (
                            <select aria-label={t('reports.builder.bucket', 'Date grouping')} value={g.bucket || 'day'} onChange={(e) => update({ groupBy: def.groupBy.map((x, j) => (j === i ? { ...x, bucket: e.target.value } : x)) })}>
                              {BUCKETS.map((b) => <option key={b} value={b}>{L.buckets[b]}</option>)}
                            </select>
                          ) : <span />}
                          <button type="button" className="rp-icon-btn" aria-label={t('reports.builder.remove', 'Remove')} onClick={() => update({ groupBy: def.groupBy.filter((_, j) => j !== i), sort: [] })}>✕</button>
                        </div>
                      );
                    })}
                    {def.groupBy.length < 3 && (
                      <button
                        type="button"
                        className="lm-btn secondary rp-add"
                        onClick={() => {
                          const next = ds.fields.find((f) => f.groupable && !def.groupBy.some((g) => g.field === f.key));
                          if (next) update({ groupBy: [...def.groupBy, { field: next.key, ...(next.type === 'date' || next.type === 'datetime' ? { bucket: 'month' } : {}) }] });
                        }}
                      >
                        {t('reports.builder.addGroup', '+ Group by')}
                      </button>
                    )}

                    <h5 style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#23324d' }}>{t('reports.builder.measures', 'Measures')}</h5>
                    {def.aggregates.map((a, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={i} className="rp-row">
                        <select aria-label={t('reports.builder.fn', 'Calculation')} value={a.fn} onChange={(e) => {
                          const fn = e.target.value;
                          const ok = fieldsForFn(fn);
                          setAgg(i, { fn, field: fn === 'count' ? undefined : (ok.some((f) => f.key === a.field) ? a.field : ok[0] && ok[0].key) });
                        }}
                        >
                          {AGG_FNS.map((fn) => <option key={fn} value={fn}>{L.fns[fn]}</option>)}
                        </select>
                        <select aria-label={t('reports.builder.measureField', 'Of')} value={a.field || ''} onChange={(e) => setAgg(i, { field: e.target.value || undefined })}>
                          {a.fn === 'count' && <option value="">{t('reports.builder.rows', 'rows')}</option>}
                          {fieldsForFn(a.fn).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select>
                        <button type="button" className="rp-icon-btn" aria-label={t('reports.builder.remove', 'Remove')} disabled={def.aggregates.length === 1 && def.groupBy.length === 0} onClick={() => update({ aggregates: withAliases(def.aggregates.filter((_, j) => j !== i)), sort: [], visualization: { type: 'table' } })}>✕</button>
                        <div className="rp-row-value">
                          <input type="text" maxLength={60} aria-label={t('reports.builder.measureLabel', 'Column name (optional)')} placeholder={t('reports.builder.measureLabel', 'Column name (optional)')} value={a.label || ''} onChange={(e) => setAgg(i, { label: e.target.value || undefined })} />
                        </div>
                      </div>
                    ))}
                    {def.aggregates.length < 10 && (
                      <button type="button" className="lm-btn secondary rp-add" onClick={() => update({ aggregates: withAliases([...def.aggregates, { fn: 'count' }]) })}>
                        {t('reports.builder.addMeasure', '+ Measure')}
                      </button>
                    )}
                  </>
                )}
              </div>
            </details>

            <details className="rp-step surface" open>
              <summary>{t('reports.builder.stepFilters', '3. Filters')}<span className="rp-step-hint">{def.filters.length || ''}</span></summary>
              <div className="rp-step-body">
                {def.filters.map((flt, i) => {
                  const ff = field(flt.field);
                  if (!ff) return null;
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i} className="rp-row">
                      <select aria-label={t('reports.filters.field', 'Field')} value={flt.field} onChange={(e) => {
                        const nf = field(e.target.value);
                        const op = OPS[opFamily(nf.type)][0];
                        update({ filters: def.filters.map((x, j) => (j === i ? { field: nf.key, op, value: defaultValue(nf, op) } : x)) });
                      }}
                      >
                        {ds.fields.filter((f) => f.filterable).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </select>
                      <select aria-label={t('reports.filters.op', 'Condition')} value={flt.op} onChange={(e) => update({ filters: def.filters.map((x, j) => (j === i ? { ...x, op: e.target.value, value: defaultValue(ff, e.target.value) } : x)) })}>
                        {OPS[opFamily(ff.type)].map((op) => <option key={op} value={op}>{L.ops[op]}</option>)}
                      </select>
                      <button type="button" className="rp-icon-btn" aria-label={t('reports.builder.remove', 'Remove')} onClick={() => update({ filters: def.filters.filter((_, j) => j !== i) })}>✕</button>
                      <div className="rp-row-value">
                        <FilterValue field={ff} filter={flt} t={t} onChange={(value) => update({ filters: def.filters.map((x, j) => (j === i ? { ...x, value } : x)) })} />
                      </div>
                    </div>
                  );
                })}
                {def.filters.length < 20 && (
                  <button
                    type="button"
                    className="lm-btn secondary rp-add"
                    onClick={() => {
                      const f0 = field(ds.defaultDateField) || ds.fields.find((f) => f.filterable);
                      const op = OPS[opFamily(f0.type)][0];
                      update({ filters: [...def.filters, { field: f0.key, op, value: defaultValue(f0, op) }] });
                    }}
                  >
                    {t('reports.filters.add', '+ Filter')}
                  </button>
                )}
              </div>
            </details>

            <details className="rp-step surface">
              <summary>{t('reports.builder.stepSort', '4. Sort & limit')}</summary>
              <div className="rp-step-body">
                <div className="rp-row">
                  <select aria-label={t('reports.builder.sortBy', 'Sort by')} value={def.sort[0]?.field || ''} onChange={(e) => update({ sort: e.target.value ? [{ field: e.target.value, dir: def.sort[0]?.dir || 'desc' }] : [] })}>
                    <option value="">{t('reports.builder.sortDefault', 'Default order')}</option>
                    {cols.filter((c) => !c.masked).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                  <select aria-label={t('reports.builder.sortDir', 'Direction')} value={def.sort[0]?.dir || 'desc'} disabled={!def.sort[0]} onChange={(e) => update({ sort: [{ field: def.sort[0].field, dir: e.target.value }] })}>
                    <option value="desc">{t('reports.builder.desc', 'High → low / newest')}</option>
                    <option value="asc">{t('reports.builder.asc', 'Low → high / oldest')}</option>
                  </select>
                  <span />
                </div>
                <label className="fo-field">
                  {t('reports.builder.limit', 'Maximum rows (blank = up to the limit)')}
                  <input type="number" min="1" value={def.limit || ''} onChange={(e) => update({ limit: e.target.value ? Number(e.target.value) : null })} />
                </label>
              </div>
            </details>

            <details className="rp-step surface">
              <summary>{t('reports.builder.stepChart', '5. Chart')}<span className="rp-step-hint">{L.viz[def.visualization.type]}</span></summary>
              <div className="rp-step-body">
                <div className="rp-mode" role="group" aria-label={t('reports.builder.chartType', 'Chart type')}>
                  {VIZ.map((v) => (
                    <button
                      key={v}
                      type="button"
                      aria-pressed={def.visualization.type === v}
                      disabled={v !== 'table' && numericCols.length === 0}
                      onClick={() => update({ visualization: defaultViz(v, def, cols, numericCols) })}
                    >
                      {L.viz[v]}
                    </button>
                  ))}
                </div>
                {(def.visualization.type === 'bar' || def.visualization.type === 'line') && (
                  <label className="fo-field">
                    {t('reports.builder.xAxis', 'Across (x-axis)')}
                    <select value={def.visualization.x || ''} onChange={(e) => update({ visualization: { ...def.visualization, x: e.target.value } })}>
                      {cols.filter((c) => !(def.visualization.y || []).includes(c.key)).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </label>
                )}
                {def.visualization.type !== 'table' && (
                  <fieldset className="fo-field" style={{ border: 0, padding: 0, margin: 0 }}>
                    <legend style={{ fontSize: '0.8rem', color: '#23324d' }}>{t('reports.builder.yAxis', 'Show')}</legend>
                    <div className="rp-chips">
                      {numericCols.filter((c) => c.key !== def.visualization.x).map((c) => {
                        const on = (def.visualization.y || []).includes(c.key);
                        return (
                          <label key={c.key} className="rp-chip">
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => {
                                const y = on ? def.visualization.y.filter((k) => k !== c.key) : [...(def.visualization.y || []), c.key];
                                if (y.length) update({ visualization: { ...def.visualization, y } });
                              }}
                            />
                            {c.label}
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                )}
              </div>
            </details>
          </div>
        )}

        <section className="surface" aria-labelledby="rp-preview-title">
          <div className="rp-head">
            <h4 id="rp-preview-title">{editable ? t('reports.builder.preview', 'Preview') : t('reports.builder.results', 'Results')}</h4>
            {perms.export && (mode !== 'new' || perms.create) && (
              <ExportBar onExport={doExport} disabled={!def && mode !== 'template'} />
            )}
          </div>
          {!perms.export && (
            <p className="surface-muted perf-small">{t('reports.builder.noExport', 'Your role cannot export reports.')}</p>
          )}
          {(previewing || heavyRunId) && (
            <p className="rp-status" role="status">
              {heavyRunId ? t('reports.builder.heavy', 'This report is large, so it is running in the background…') : t('reports.builder.running', 'Running…')}
            </p>
          )}
          {previewError && <div className="note error" role="alert">{previewError}</div>}
          {editable && def && !previewable(def) && (
            <p className="surface-muted">{t('reports.builder.pickColumns', 'Choose at least one column to see a preview.')}</p>
          )}
          <ReportResult result={result} onPage={setPage} />
        </section>
      </div>

      {scheduleFor && (
        <ScheduleModal reportId={scheduleFor} reportName={meta.name || title} timezone={timezone} canShare={!!perms.share} onClose={() => setScheduleFor(null)} />
      )}
      {shareFor && (
        <ShareModal reportId={shareFor} reportName={meta.name || title} onClose={() => setShareFor(null)} />
      )}
      {footer}
    </div>
  );
}
